/**
 * Chat service - handles SSE communication with the chat bridge
 */

const CHAT_API_URL = process.env.VUE_APP_CHAT_API_URL || 'http://localhost:3001';

class ChatService {
  /**
   * Send a message and handle streaming response
   * @param {string} message - The user's message
   * @param {string} sessionId - Session identifier
   * @param {string} token - JWT token
   * @param {object} callbacks - Callback functions for handling response
   */
  async sendMessage(message, sessionId, token, callbacks) {
    const { onText, onToolUse, onToolResult, onError, onDone } = callbacks;

    try {
      const response = await fetch(`${CHAT_API_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message, sessionId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error ${response.status}`);
      }

      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              switch (data.type) {
                case 'text':
                  onText?.(data.content);
                  break;
                case 'tool_use':
                  onToolUse?.(data.tool);
                  break;
                case 'tool_result':
                  onToolResult?.(data.result);
                  break;
                case 'error':
                  onError?.({ message: data.error });
                  break;
                case 'done':
                  onDone?.();
                  return;
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', parseError);
            }
          }
        }
      }

      onDone?.();
    } catch (error) {
      console.error('Chat service error:', error);
      onError?.(error);
      onDone?.();
    }
  }

  /**
   * Get user's chat sessions
   * @param {string} token - JWT token
   * @returns {Promise<array>} - Array of session summaries
   */
  async getSessions(token) {
    const response = await fetch(`${CHAT_API_URL}/api/chat/sessions`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get sessions: ${response.status}`);
    }

    const data = await response.json();
    return data.sessions;
  }

  /**
   * Clear a chat session
   * @param {string} sessionId - Session to clear
   * @param {string} token - JWT token
   */
  async clearSession(sessionId, token) {
    const response = await fetch(`${CHAT_API_URL}/api/chat/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to clear session: ${response.status}`);
    }
  }

  /**
   * Check if the chat service is available
   * @returns {Promise<boolean>}
   */
  async healthCheck() {
    try {
      const response = await fetch(`${CHAT_API_URL}/api/chat/health`);
      return response.ok;
    } catch {
      return false;
    }
  }
}

export default new ChatService();
